import { useState, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Default maximum number of days (inclusive) the range may span.
const DEFAULT_MAX_RANGE_DAYS = 5;

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const DAY_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

const formatDay = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return DAY_FORMAT.format(date);
};

/** e.g. "July 16, 2026" or "July 14, 2026 – July 16, 2026". */
const formatSelectedTimeline = (range) => {
  if (!range?.start || !range?.end) return '';
  const startLabel = formatDay(range.start);
  const endLabel = formatDay(range.end);
  if (!startLabel || !endLabel) return '';
  if (
    range.start.getFullYear() === range.end.getFullYear()
    && range.start.getMonth() === range.end.getMonth()
    && range.start.getDate() === range.end.getDate()
  ) {
    return startLabel;
  }
  return `${startLabel} – ${endLabel}`;
};

/**
 * Day-granularity range picker. By default spans up to five days within a
 * rolling two-year lookback. Pass `maxRangeDays={null}` (or a large number)
 * to allow any span inside that window. Pass `lookbackMonths` to shorten the
 * earliest selectable day, for example 6 on Solar Inverter Performance.
 */
const DayRangePicker = ({
  range = null,
  setRange = () => {},
  maxRangeDays = DEFAULT_MAX_RANGE_DAYS,
  lookbackMonths = null,
}) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [startDate, setStartDate] = useState(range?.start ?? null);
  const [endDate, setEndDate] = useState(range?.end ?? null);
  const wrapperRef = useRef(null);
  const timelineLabel = useMemo(() => formatSelectedTimeline(range), [range]);
  const spanLimit = maxRangeDays == null || maxRangeDays <= 0 ? null : maxRangeDays;

  useEffect(() => {
    setStartDate(range?.start ?? null);
    setEndDate(range?.end ?? null);
  }, [range]);

  const today = useMemo(() => new Date(), []);

  const earliestSelectable = useMemo(() => {
    if (lookbackMonths != null && lookbackMonths > 0) {
      return new Date(today.getFullYear(), today.getMonth() - lookbackMonths, today.getDate());
    }
    return new Date(today.getFullYear() - 2, today.getMonth(), today.getDate());
  }, [today, lookbackMonths]);
  const latestSelectable = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999),
    [today],
  );

  const effectiveMinDate = useMemo(() => {
    if (startDate && !endDate && spanLimit != null) {
      const minFromStart = addDays(startDate, -(spanLimit - 1));
      return minFromStart > earliestSelectable ? minFromStart : earliestSelectable;
    }
    return earliestSelectable;
  }, [startDate, endDate, earliestSelectable, spanLimit]);

  const effectiveMaxDate = useMemo(() => {
    if (startDate && !endDate && spanLimit != null) {
      const maxFromStart = addDays(startDate, spanLimit - 1);
      return maxFromStart < latestSelectable ? maxFromStart : latestSelectable;
    }
    return latestSelectable;
  }, [startDate, endDate, latestSelectable, spanLimit]);

  useEffect(() => {
    if (!isPickerOpen) return undefined;
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPickerOpen]);

  const handleRangeChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    if (start && end) {
      const normalizedStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const normalizedEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);
      setRange({ start: normalizedStart, end: normalizedEnd });
      setIsPickerOpen(false);
    }
  };

  const isRangeActive = Boolean(range?.start && range?.end);

  return (
    <div className="monthrange-picker" ref={wrapperRef}>
      <button
        type="button"
        aria-label={timelineLabel ? `Select day range, ${timelineLabel}` : 'Select day range'}
        aria-haspopup="dialog"
        aria-expanded={isPickerOpen}
        className={`monthrange-picker__toggle ${isRangeActive ? 'active' : ''}`}
        onClick={() => setIsPickerOpen((prev) => !prev)}
      >
        <FontAwesomeIcon icon={faCalendar} />
        {timelineLabel ? (
          <span className="monthrange-picker__label">{timelineLabel}</span>
        ) : null}
      </button>
      {isPickerOpen && (
        <div className="monthrange-picker__popover">
          <DatePicker
            selected={startDate}
            onChange={handleRangeChange}
            startDate={startDate}
            endDate={endDate}
            minDate={effectiveMinDate}
            maxDate={effectiveMaxDate}
            dateFormat="MM/dd/yyyy"
            selectsRange
            swapRange
            inline
          />
        </div>
      )}
    </div>
  );
};

export default DayRangePicker;
